import { makeAutoObservable, runInAction, toJS, reaction } from 'mobx';
import { RootStore } from 'store/rootStore';
import { Cart } from 'types/cartService';
import { Config, DomainProductLine, InputFilter } from 'types/domainService';
import { INITIAL_CART_OBJECT } from 'store/domain/cartStore';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { EStoreFilters, PortfolioRequest } from 'types/productService';
import {
  BrowsingInfo,
  MarketingTag,
  Portfolio,
  ProductLineAttr,
  RateSegment,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { StandardFormChangeEvent } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { INITIAL_DOMAIN_CONFIGURATION } from 'store/domain/domainStore';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { QuoteStatus } from 'types/zywaveService';
import { getCatalog, getPortfolios } from 'api/operations/productServiceV2';
import { getQuoteStatus } from 'api/operations/zywaveService';

export class PopularPlansStore {
  rootStore: RootStore;

  loading = false;
  loadingPortfolioId = ''; // loading inside a popular plan card

  productLines: DomainProductLine[] = [];
  cart: Cart = INITIAL_CART_OBJECT;
  currentEffectiveDate = '';
  currentZipCode = '';
  currentProductLineId?: ProductLineId;
  domainConfig: Config = INITIAL_DOMAIN_CONFIGURATION;

  // Attributes for all product line ids
  productLineAttrs: ProductLineAttr[] = [];
  marketingTags: MarketingTag[] = [];
  allPortfolios: Portfolio[] = [];
  eStoreFilters: EStoreFilters[] = [];

  // Adjusting the height dynamically to prevent excessive space in a row dedicated to displaying advantages.
  // The challenge here is maintaining uniform row heights across columns, which cannot be accomplished solely through CSS.
  // In one column, there are two rows with varying heights: the last and largest row is set to 1fr,
  // while the another row (the advantages list) necessitates manual height calculation to accommodate its dynamic content.
  highestAdvantagesRowHeight = 0;

  /**
   * Modal that will indicate for the user that we are waiting for rates
   */
  continuousPullingRatesModalOpened = false;
  /**
   * Counter for amount of calls to Zywave, because API is unreliable and our app might stuck on pulling rates
   */
  calculateRatesAttempts = 0;

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  private setLoadingPortfolioId(id: string): void {
    this.loadingPortfolioId = id;
  }

  private setCurrentEffectiveDate(effectiveDate: string): void {
    this.currentEffectiveDate = effectiveDate;
  }

  private setCurrentZipCode(zipCode: string): void {
    this.currentZipCode = zipCode;
  }

  private setCurrentProductLineId(currentProductLineId: ProductLineId): void {
    this.currentProductLineId = currentProductLineId;
  }

  private setDomainConfig(config: Config): void {
    this.domainConfig = config;
  }

  private incrementRatesAttempts(): void {
    this.calculateRatesAttempts += 1;
  }

  private resetRatesAttempts(): void {
    this.calculateRatesAttempts = 0;
  }

  private getCalculateRatesAttempts(): number {
    return this.calculateRatesAttempts;
  }

  private hasMockRates(): boolean {
    return this.allPortfolios.some((portfolio) =>
      portfolio.products.some((product) => product.bcDependentData[0].rates.some((rate) => rate.key === 'MOCK')),
    );
  }

  public setContinuousPullingRatesModal(opened: boolean): void {
    this.continuousPullingRatesModalOpened = opened;
  }

  public setHighestAdvantagesRowHeight(height: number): void {
    this.highestAdvantagesRowHeight = height;
  }

  // Initial method tha collects all required info for the page
  public async collectInitialData(cartId: string, productLineId: ProductLineId): Promise<void> {
    this.setLoading(true);
    this.setCurrentProductLineId(productLineId); // set value right after page render
    const rateSegment = PRODUCT_LINES_CONTENT_MAP[productLineId].rateSegment;
    const sponsorId = this.rootStore.domainStore.domainHeader.id;

    const res = await this.rootStore.cartStore.getCart(cartId);
    await runInAction(async () => {
      if (!res.data) {
        throw new Error(`cart with such id doesn't exist: ${cartId}`);
      }
      this.cart = res.data.getCart;
      const convertedCart = toJS(this.cart);
      const { effectiveDate, zipCode, state, county } = convertedCart.lastUpdateBrowsingCriteria;

      // Fill Bc info block
      this.setCurrentEffectiveDate(effectiveDate);
      this.setCurrentZipCode(zipCode);

      const requests = [
        this.getProductLines(
          {
            effectiveDate,
            zipCode,
            state,
            county,
            agencyId: '',
            sponsorId,
          },
          rateSegment,
        ),
        this.getCatalog(
          convertedCart.lastUpdateBrowsingCriteria.effectiveDate,
          rateSegment,
          convertedCart.lastUpdateBrowsingCriteria.state,
        ),
        this.getConfig(sponsorId, effectiveDate),
      ];

      await Promise.all(requests);
      // Show a link to the shopping cart in the header and provide the necessary data for redirection
      this.rootStore.domainStore.setDataForCartRedirect(cartId, convertedCart.groupId, true);
      this.rootStore.domainStore.changeCartItemsQuantity(convertedCart.cartItems.length);

      void runInAction(async () => {
        await this.collectPortfoliosInfo(productLineId);
        this.setLoading(false);
      });
    });
  }

  private async collectPortfoliosInfo(productLineId: ProductLineId): Promise<void> {
    // const convertedCart = toJS(this.cart);
    const sponsorId = this.rootStore.domainStore.domainHeader.id;
    const productLineCriteria = this.cart.lastUpdateBrowsingCriteria[productLineId];

    const filteringBC = this.eStoreFilters
      .filter((filter) => filter.type === 'BROWSING_CRITERIA' && filter.filters.length > 0) // search for the BC that acts as a filter
      .filter((filter) => filter.productLines.includes(productLineId)) // only for the elected product line
      .filter((filter) => !productLineCriteria || !productLineCriteria[filter.key]) // if we already have filtering key in the Cart BC don't override it
      .map((filter) => ({ [filter.key]: filter.filters[0].value })) // create object with initial values
      .reduce((acc, obj) => ({ ...acc, ...obj }), {});

    this.cart.lastUpdateBrowsingCriteria[productLineId] = {
      ...this.cart.lastUpdateBrowsingCriteria[productLineId],
      ...filteringBC,
    };
    await this.getPortfolios({
      bc: this.cart.lastUpdateBrowsingCriteria,
      rateSegment: this.cart.rateSegment,
      productLineId,
      sponsorId,
      agencyId: '',
      onlyTagged: true,
      groupId: this.cart.groupId,
    });
  }

  private async getCatalog(effectiveDate: string, rateSegment: RateSegment, state: string): Promise<void> {
    const res = await getCatalog(this.rootStore.domainStore.domainHeader.id, effectiveDate, rateSegment, state);
    runInAction(() => {
      const catalog = res.data.getCatalog;
      this.marketingTags = catalog.marketingTags;
      this.productLineAttrs = catalog.productLineAttributes;
      this.eStoreFilters = catalog.eStoreFilters;
    });
  }

  private async getPortfolios(request: PortfolioRequest): Promise<void> {
    const res = await getPortfolios(request);
    runInAction(() => {
      this.allPortfolios = res.data.getPortfolios;
      if (this.hasMockRates()) this.setContinuousPullingRatesModal(true);
    });
  }

  private updateCart(): void {
    void this.rootStore.cartStore.updateBC(
      this.cart.id,
      this.cart.lastUpdateBrowsingCriteria,
      this.rootStore.domainStore.domainHeader.id,
    );
  }

  private async getConfig(sponsorId: string, effectiveDate: string): Promise<void> {
    const config = await this.rootStore.domainStore.getConfigByDate(sponsorId, effectiveDate);
    this.setDomainConfig(config);
  }

  private async getProductLines(inputFilter: InputFilter, rateSegment: RateSegment): Promise<void> {
    const res = await this.rootStore.domainStore.getDomainProductLine(inputFilter);
    runInAction(() => {
      this.productLines = res.filter((line) => line.rateSegment === rateSegment); // filter out product lines that have different rate segment
    });
  }

  public async onProductLineChange(e: StandardFormChangeEvent): Promise<void> {
    const productLineId = e.target.value as ProductLineId;
    this.setLoading(true);
    this.setCurrentProductLineId(productLineId);
    this.setHighestAdvantagesRowHeight(0);
    await this.collectPortfoliosInfo(productLineId);
    this.setLoading(false);
  }

  public async onAddToProposal(portfolioId: string, portfolioName: string): Promise<void> {
    this.setLoadingPortfolioId(portfolioId);
    if (!this.currentProductLineId) {
      throw new Error('Unsupported ProductLineId');
    }
    const { id, lastUpdateBrowsingCriteria } = this.cart;
    const currentPortfolio = this.allPortfolios.find((portfolio) => portfolio.id === portfolioId);
    const convertedPortfolio = toJS(currentPortfolio);
    const bcVersion = convertedPortfolio && Object.values(convertedPortfolio.bcVersions)[0];

    const res = await this.rootStore.cartStore.addPortfolioToCart(
      {
        cartId: id,
        portfolioId,
        portfolioName,
        browsingCriteria: { ...lastUpdateBrowsingCriteria, ...bcVersion },
        productLineId: this.currentProductLineId,
        bcIndex: null,
      },
      this.rootStore.domainStore.domainHeader.id,
    );
    runInAction(() => {
      const currentCart = res.data?.addPortfolioConfigToCart;
      if (currentCart) {
        this.cart = currentCart;
        this.rootStore.domainStore.changeCartItemsQuantity(currentCart.cartItems.length);
      }
      this.setLoadingPortfolioId('');
    });
  }

  public async onRemoveFromProposal(portfolioId: string): Promise<void> {
    this.setLoadingPortfolioId(portfolioId);
    const res = await this.rootStore.cartStore.removePortfolioFromCart(this.cart.id, portfolioId, false);
    runInAction(() => {
      const currentCart = res.data?.removePortfolioConfigFromCart;
      if (currentCart) {
        this.cart = currentCart;
        this.rootStore.domainStore.changeCartItemsQuantity(currentCart.cartItems.length);
      }
      this.setLoadingPortfolioId('');
    });
  }

  // eslint-disable-next-line class-methods-use-this
  public onViewPlanDetails(portfolioId: string, cartId: string, pushFn: (link: string) => void): void {
    // Helps to make redirect without full page reload
    pushFn(
      `/product-compare?type=popular&portfolioId=${portfolioId}&cartId=${cartId}&productLineId=${this
        .currentProductLineId!}&groupId=${this.cart.groupId}&cartType=popular`,
    );
  }

  public getBrowsingCriteriaFilters(): Record<string, OptionType[]> {
    return this.eStoreFilters
      .filter((filter) => filter.type === 'BROWSING_CRITERIA' && filter.filters.length > 0)
      .filter((filter) => !!this.currentProductLineId && filter.productLines.includes(this.currentProductLineId))
      .reduce((acc, obj) => {
        const values = obj.filters.map(({ value, displayName }) => ({ value, label: displayName }));
        return { ...acc, [obj.key]: values };
      }, {} as Record<string, OptionType[]>);
  }

  public getFilterValue(key: string, filters: OptionType[]): string {
    if (this.currentProductLineId && this.cart.lastUpdateBrowsingCriteria[this.currentProductLineId]) {
      const value = (this.cart.lastUpdateBrowsingCriteria[this.currentProductLineId] as BrowsingInfo)[key];
      return value || filters[0].value;
    }
    return filters[0].value;
  }

  public async onFilterChange(key: string, value: string): Promise<void> {
    this.setLoading(true);
    if (!this.currentProductLineId) throw new Error('Filter Change without established Product line is unavailable');
    this.cart.lastUpdateBrowsingCriteria[this.currentProductLineId] = {
      ...this.cart.lastUpdateBrowsingCriteria[this.currentProductLineId],
      ...{ [key]: value },
    };
    const requests = [this.updateCart(), this.getPortfolios(this.assemblePortfolioRequest())];
    await Promise.all(requests);
    this.setLoading(false);
  }

  private assemblePortfolioRequest = (): PortfolioRequest => {
    return {
      bc: this.cart.lastUpdateBrowsingCriteria,
      rateSegment: this.cart.rateSegment,
      productLineId: this.currentProductLineId || 'GroupMedical',
      sponsorId: this.rootStore.domainStore.domainHeader.id,
      agencyId: '',
      onlyTagged: true,
      groupId: this.cart.groupId,
    };
  };

  public async checkZywaveQuotingStatus(): Promise<QuoteStatus> {
    if (!this.currentProductLineId) {
      throw new Error('Unsupported ProductLineId');
    }
    return getQuoteStatus(this.cart.groupId, this.currentProductLineId, this.cart.lastUpdateBrowsingCriteria);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this, { rootStore: false }, { autoBind: true });

    reaction(
      () => this.continuousPullingRatesModalOpened,
      (modalOpened) => {
        if (modalOpened) {
          // create request for the Portfolios outside interval to avoid warning that it's not in reactive context
          const request: PortfolioRequest = this.assemblePortfolioRequest();
          // if rates mocked, that means that it's Zywave plans, so we have to wait
          // until they will calculate rates. Code below make calls to ZywaveService
          // each 5 seconds and stops, only when the response is RATED, or it was more
          // than 10 attempts
          const interval = setInterval(() => {
            void this.checkZywaveQuotingStatus().then(async (status) => {
              if (status === QuoteStatus.RATED) {
                clearInterval(interval);

                // After rates calculated we have to update portfolio rates,
                // so here we make NO-CACHE request for portfolios,
                // otherwise u will receive mock rates again
                this.incrementRatesAttempts(); // hack, so ticker won't pause on one value

                await this.getPortfolios(request);
                this.setContinuousPullingRatesModal(false);
                this.resetRatesAttempts();
              } else if (this.getCalculateRatesAttempts() < 10) {
                this.incrementRatesAttempts();
              } else {
                throw new Error('Too many attempts to rate Quote');
              }
            });
          }, 5000);
        }
      },
    );
  }
}
